<template>
  <div class="view-job-container">
    <!-- Back Button -->
    <v-btn icon class="back-button" @click="openBackConfirmation">
      <v-icon>mdi-arrow-left</v-icon>
    </v-btn>

    <!-- Job Details -->
    <div v-if="job" style="margin-bottom: 50px">
      <v-col cols="12" md="12" lg="10" xl="8" class="pa-2">
        <v-row justify="center">
          <h2 class="red--text font-weight-bold montserrat-title">
            {{ job.jobName }} - Job #{{ job.jobNumber }}
          </h2>
        </v-row>
      </v-col>

      <v-card style="margin-top: 50px">
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <!-- Job Details Section -->
            <h3 class="section-title montserrat-title">Job Details</h3>
            <v-row dense>
              <!-- Job Number -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.jobNumber"
                  label="Job Number"
                  dense
                  outlined
                  prepend-inner-icon="mdi-cube-outline"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  readonly
                ></v-text-field>
              </v-col>

              <!-- Seal Type -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.jobName"
                  label="Seal Type"
                  dense
                  outlined
                  prepend-inner-icon="mdi-cube-outline"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>

              <!-- Shaft Size -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.ShaftSize"
                  label="Shaft Size"
                  dense
                  outlined
                  prepend-inner-icon="mdi-clock"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>

              <!-- Customer Name -->
              <v-col cols="12" md="3" class="pa-2">
                <v-autocomplete
                  v-model="job.customerName"
                  :items="customers"
                  item-text="companyName"
                  item-value="companyName"
                  label="Customer Name"
                  dense
                  outlined
                  prepend-inner-icon="mdi-account"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-autocomplete>
              </v-col>

              <!-- Contact Person -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.customerNumber"
                  label="Contact Person"
                  dense
                  outlined
                  prepend-inner-icon="mdi-account"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>

              <!-- Engineer -->
              <v-col cols="12" md="3" class="pa-2">
                <v-select
                  v-model="job.engineer"
                  :items="engineers"
                  label="Engineer"
                  item-text="fullName"
                  item-value="docId"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-select>
              </v-col>

              <!-- Technician -->
              <v-col cols="12" md="3" class="pa-2">
                <v-select
                  v-model="job.technician"
                  :items="technicians"
                  label="Technician"
                  item-text="fullName"
                  item-value="docId"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-select>
              </v-col>

              <!-- Salesperson -->
              <v-col cols="12" md="3" class="pa-2">
                <v-select
                  v-model="job.salesperson"
                  :items="salespersons"
                  label="Salesperson"
                  item-text="fullName"
                  item-value="docId"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-select>
              </v-col>

              <!-- Urgency (Priority) -->
              <v-col cols="12" md="3" class="pa-2">
                <v-select
                  v-model="job.urgency"
                  :items="urgencyOptions"
                  label="Priority"
                  dense
                  outlined
                  prepend-inner-icon="mdi-alert"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-select>
              </v-col>

              <!-- Set Rate -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model.number="job.setRate"
                  label="Set Rate"
                  dense
                  outlined
                  prepend-inner-icon="mdi-currency-usd"
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>

              <!-- Labour Hours -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model.number="job.timeToComplete"
                  label="Labour Hours"
                  dense
                  outlined
                  prepend-inner-icon="mdi-clock"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>

              <!-- Status -->
              <v-col cols="12" md="3" class="pa-2">
                <v-select
                  v-model="job.status"
                  :items="statusOptions"
                  label="Status"
                  outlined
                  dense
                  prepend-inner-icon="mdi-timeline-clock"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  @change="handleStatusChange"
                ></v-select>
              </v-col>

              <!-- Current Date -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.currentDate"
                  label="Inspection Date"
                  dense
                  outlined
                  prepend-inner-icon="mdi-calendar"
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>

              <!-- Total Lead Time -->
              <v-col cols="12" md="3" class="pa-2">
                <v-select
                  v-model="job.TotalLead"
                  :items="leadTimeOptions"
                  label="Total Lead Time"
                  dense
                  outlined
                  prepend-inner-icon="mdi-clock"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-select>
              </v-col>

              <!-- SERIAL NUMBER FIELDS (visible when Ready) -->
              <v-col cols="12" md="3" class="pa-2" v-if="showSerialFields">
                <v-text-field
                  v-model="primaryKey"
                  label="Certificate Number"
                  dense
                  outlined
                  prepend-inner-icon="mdi-key"
                  class="montserrat-input"
                  :rules="[rules.required, rules.qcPrefix]"
                  readonly
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="linkKey"
                  label="Serial Number"
                  dense
                  outlined
                  prepend-inner-icon="mdi-link"
                  class="montserrat-input"
                  :rules="[rules.required, rules.qcPrefix]"
                  @input="ensureQCPrefix"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Additional Serial Fields -->
            <v-row dense v-if="showSerialFields">
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="elastomers"
                  label="Elastomers"
                  dense
                  outlined
                  prepend-inner-icon="mdi-cog"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="ibs"
                  label="IBS Face Material"
                  dense
                  outlined
                  prepend-inner-icon="mdi-cog-outline"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="ibr"
                  label="IBR Face Material"
                  dense
                  outlined
                  prepend-inner-icon="mdi-cog-outline"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row dense v-if="showSerialFields">
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="obs"
                  label="OBS Face Material"
                  dense
                  outlined
                  prepend-inner-icon="mdi-cog-outline"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="obr"
                  label="OBR Face Material"
                  dense
                  outlined
                  prepend-inner-icon="mdi-cog-outline"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Serial Notes -->
            <v-row v-if="showSerialFields">
              <v-col cols="12" class="pa-2">
                <v-textarea
                  v-model="notes"
                  label="Serial Notes"
                  auto-grow
                  rows="2"
                  dense
                  outlined
                  prepend-inner-icon="mdi-note"
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-textarea>
              </v-col>
            </v-row>

            <!-- Job Description -->
            <v-row>
              <v-col cols="12" class="pa-2">
                <v-textarea
                  v-model="job.jobDescription"
                  label="Job Description"
                  outlined
                  dense
                  prepend-inner-icon="mdi-format-list-bulleted"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  style="min-height: 100px;"
                  :readonly="isInspection"
                ></v-textarea>
              </v-col>
            </v-row>

            <!-- Inspection Checklist & Download Photos -->
            <v-row class="pa-2">
              <v-btn dark color="blue" @click="openAndDownloadPhotos" class="ma-2">
                <v-icon left>mdi-download</v-icon>Download All Photos
              </v-btn>
              <v-btn dark color="blue" @click="openChecklistDialog" class="ma-2">
                <v-icon left>mdi-clipboard-check</v-icon>View Inspection Checklist
              </v-btn>
            </v-row>

            <!-- Checklist component -->
            <display-checklist
              :jobNumber="job.jobNumber"
              v-model="checklistDialog"
            />

            <!-- Labour & Stock Sections -->
            <div v-if="!hideLabourStock">
              <!-- Labour Items Table -->
              <v-row class="my-4">
                <h2 class="red--text mx-12">Labour Items</h2>
              </v-row>
              <v-simple-table class="pa-1 ma-0">
                <thead>
                  <tr>
                    <th class="text-left">Qty</th>
                    <th class="text-left">Part Size</th>
                    <th class="text-left">Description</th>
                    <th class="text-left">Job Category</th>
                    <th class="text-left">Unit Price</th>
                    <th class="text-left">Total Price</th>
                    <th class="text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(labourItem, index) in job.labourItems" :key="index">
                    <td>
                      <v-text-field
                        v-model.number="labourItem.quantity"
                        type="number"
                        min="1"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required, rules.positive]"
                        @input="calculateLabourTotalPrice(labourItem)"
                        style="width: 100px;"
                        :readonly="isInspection"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="labourItem.partSize"
                        type="number"
                        min="0"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required, rules.nonNegative]"
                        @input="calculateLabourTotalPrice(labourItem)"
                        style="width: 120px;"
                        :readonly="isInspection"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model="labourItem.description"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required]"
                        :readonly="isInspection"
                        style="width: 400px;"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-select
                        v-model="labourItem.jobCategory"
                        :items="jobCategories.labour"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required]"
                        @change="setMarkup(labourItem)"
                        style="width: 300px;"
                        :readonly="isInspection"
                      ></v-select>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="labourItem.unitPrice"
                        type="number"
                        dense
                        outlined
                        class="montserrat-input"
                        :readonly="labourItem.jobCategory !== 'Custom' || isInspection"
                        style="width: 150px;"
                        @input="updateLabourTotalPrice(labourItem)"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="labourItem.totalPrice"
                        type="number"
                        dense
                        outlined
                        class="montserrat-input"
                        readonly
                        style="width: 200px;"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-btn
                        icon
                        color="red"
                        @click="confirmAction('removeLabour', index)"
                        :disabled="isInspection || isSalesperson"
                        style="margin-bottom: 30px"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <!-- Add Labour Item & Subtotal -->
              <v-row justify="start" class="pa-2">
                <v-btn
                  dark
                  color="blue"
                  @click="confirmAction('addLabour')"
                  class="ma-2"
                  :disabled="isInspection || isSalesperson"
                >
                  <v-icon left>mdi-plus</v-icon>Add Labour Item
                </v-btn>
              </v-row>
              <v-row justify="end">
                <v-col cols="12" md="3" class="pa-2">
                  <h3 class="section-title montserrat-title">Labour Subtotal</h3>
                  <v-text-field
                    v-model.number="job.labourSubTotal"
                    label="Labour Subtotal"
                    dense
                    outlined
                    prepend-inner-icon="mdi-cash-multiple"
                    class="montserrat-input"
                    readonly
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Stock Items Table -->
              <v-row class="my-4">
                <h2 class="red--text mx-12">Stock/Parts Items</h2>
              </v-row>
              <v-simple-table class="pa-1 ma-0">
                <thead>
                  <tr>
                    <th class="text-left">Qty</th>
                    <th class="text-left">Code</th>
                    <th class="text-left">Lead Time</th>
                    <th class="text-left">Stock Item</th>
                    <th class="text-left">Unit Price</th>
                    <th class="text-left">Total Price</th>
                    <th class="text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(stockItem, index) in job.stockItems" :key="index">
                    <td>
                      <v-text-field
                        v-model.number="stockItem.quantity"
                        type="number"
                        min="1"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required, rules.positive]"
                        @input="calculateStockTotalPrice(stockItem)"
                        style="width: 100px;"
                        :readonly="isInspection"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model="stockItem.code"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required]"
                        :readonly="isInspection"
                        style="width: 150px;"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-select
                        v-model="stockItem.leadTime"
                        :items="leadTimeOptions"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required]"
                        @change="calculateStockTotalPrice(stockItem)"
                        style="width: 150px;"
                        :readonly="isInspection"
                      ></v-select>
                    </td>
                    <td>
                      <v-select
                        v-model="stockItem.jobCategory"
                        :items="jobCategories.stock"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required]"
                        @change="setMarkup(stockItem)"
                        style="width: 250px;"
                        :readonly="isInspection"
                      ></v-select>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="stockItem.unitPrice"
                        type="number"
                        dense
                        outlined
                        class="montserrat-input"
                        :readonly="stockItem.jobCategory !== 'Custom' || isInspection"
                        style="width: 120px;"
                        @input="updateStockTotalPrice(stockItem)"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="stockItem.totalPrice"
                        type="number"
                        dense
                        outlined
                        class="montserrat-input"
                        readonly
                        style="width: 150px;"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-btn
                        icon
                        color="red"
                        @click="confirmAction('removeStock', index)"
                        :disabled="isInspection || isSalesperson"
                        style="margin-bottom: 30px"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <!-- Add Stock Item & Subtotal -->
              <v-row justify="start" class="pa-2">
                <v-btn
                  dark
                  color="blue"
                  @click="confirmAction('addStock')"
                  class="ma-2"
                  :disabled="isInspection || isSalesperson"
                >
                  <v-icon left>mdi-plus</v-icon>Add Stock/Part Item
                </v-btn>
              </v-row>
              <v-row justify="end">
                <v-col cols="12" md="3" class="pa-2">
                  <h3 class="section-title montserrat-title">Stock/Parts Subtotal</h3>
                  <v-text-field
                    v-model.number="job.stockSubTotal"
                    label="Stock/Parts Subtotal"
                    dense
                    outlined
                    prepend-inner-icon="mdi-cash-multiple"
                    class="montserrat-input"
                    readonly
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Grand Total -->
              <v-row justify="end">
                <v-col cols="12" md="3" class="pa-2">
                  <h3 class="section-title montserrat-title">Grand Total</h3>
                  <v-text-field
                    v-model.number="job.grandTotal"
                    label="Grand Total"
                    dense
                    outlined
                    prepend-inner-icon="mdi-cash-multiple"
                    class="montserrat-input"
                    readonly
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
            <!-- END Labour & Stock Sections -->

            <!-- Supplier Quotes Section -->
            <v-row class="pa-2">
              <v-col
                cols="12"
                v-for="(quote, index) in job.extraInfoFields"
                :key="`quote-${index}`"
                class="pa-2"
              >
                <div class="supplier-quote-item">
                  <!-- Customize supplier quote content if needed -->
                </div>
              </v-col>
            </v-row>

            <!-- Additional Fields Section -->
            <v-row>
              <v-col cols="12" md="12" class="pa-2">
                <h3 class="section-title montserrat-title">Additional Fields</h3>
              </v-col>
              <!-- Sample Number -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.sampleNumber"
                  label="Sample Number (Optional)"
                  outlined
                  dense
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
              <!-- Customer Reference -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.customerReference"
                  label="Customer Reference (Optional)"
                  outlined
                  dense
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
              <!-- Date Quoted -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.dateQuoted"
                  label="Date Quoted (Auto)"
                  outlined
                  dense
                  class="montserrat-input"
                  readonly
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Comment & Summary -->
            <v-row class="pa-2">
              <v-col cols="12" md="12" class="pa-2" v-if="!isSalesperson">
                <v-textarea
                  v-model="job.comment"
                  label="Comment (For Internal Workshop Only)"
                  outlined
                  dense
                  prepend-inner-icon="mdi-comment-text-outline"
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-textarea>
              </v-col>
            </v-row>
            <v-row class="pa-2">
              <v-col cols="12" md="12" class="pa-2">
                <v-textarea
                  v-model="job.JobSummary"
                  label="Special Notes/Specific Requirements"
                  outlined
                  dense
                  prepend-inner-icon="mdi-comment-text-outline"
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-textarea>
              </v-col>
            </v-row>

            <!-- Save Button -->
            <v-row justify="center" class="my-6">
              <v-btn
                color="blue"
                large
                @click="confirmAction('saveJobDetails')"
                class="montserrat-button"
              >
                <v-icon left>mdi-content-save</v-icon>Save Changes
              </v-btn>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
    </div>

    <!-- Loading State -->
    <div v-else class="loading-state">
      <v-progress-circular indeterminate color="blue"></v-progress-circular>
      <span class="ml-2">Loading...</span>
    </div>

    <!-- Photo Carousel Dialog -->
    <v-dialog v-model="photoDialog" max-width="800px">
      <v-card>
        <v-card-title class="headline">Job Photos</v-card-title>
        <v-card-text>
          <v-carousel v-if="!loadingPhotos && jobPhotos.length > 0" hide-delimiters>
            <v-carousel-item v-for="(photo, index) in jobPhotos" :key="index">
              <v-img :src="photo" class="carousel-photo" max-height="400px"></v-img>
            </v-carousel-item>
          </v-carousel>
          <div v-if="loadingPhotos" class="d-flex align-center">
            <v-progress-circular indeterminate color="blue"></v-progress-circular>
            <span class="ml-2">Loading photos...</span>
          </div>
          <div v-if="!loadingPhotos && jobPhotos.length === 0">
            <p>No photos available for this job.</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="blue"
            dark
            @click="downloadPhotos"
            :disabled="isInspection || isSalesperson"
          >
            <v-icon left>mdi-download</v-icon>Download All Photos
          </v-btn>
          <v-btn color="red" dark @click="photoDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Supplier Edit Dialog -->
    <v-dialog v-model="supplierEditDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Edit Supplier Details</v-card-title>
        <v-card-text>
          <v-form ref="supplierForm" v-model="supplierFormValid">
            <v-autocomplete
              v-model="editSupplier.supplier"
              :items="suppliers"
              item-text="supplierName"
              item-value="docId"
              label="Supplier"
              dense
              outlined
              prepend-inner-icon="mdi-factory"
              class="montserrat-input"
              :rules="[rules.required]"
              :readonly="isInspection"
            ></v-autocomplete>
            <v-text-field
              dense
              outlined
              label="Drawing Number"
              v-model="editSupplier.drawingNumber"
              class="montserrat-input"
              :rules="[rules.required]"
              :readonly="isInspection"
            ></v-text-field>
            <v-textarea
              dense
              outlined
              label="Special Note"
              v-model="editSupplier.specialNote"
              class="montserrat-input"
              :rules="[rules.required]"
              :readonly="isInspection"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green" dark @click="saveSupplierDetails" :disabled="isInspection">
            Save
          </v-btn>
          <v-btn color="grey" dark @click="supplierEditDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmationDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Confirm Action</v-card-title>
        <v-card-text>{{ confirmationMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="executeConfirmedAction">Yes</v-btn>
          <v-btn color="red darken-1" text @click="confirmationDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Extra Field Confirmation -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this item and associated photo?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" dark @click="deleteFieldConfirmed">Delete</v-btn>
          <v-btn color="grey" dark @click="deleteDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- File Preview Dialog -->
    <v-dialog v-model="fileDialog" max-width="600px">
      <v-card>
        <v-card-title class="headline">File Preview</v-card-title>
        <v-card-text>
          <v-img :src="selectedFileUrl" max-width="100%"></v-img>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" dark @click="fileDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Back Confirmation Dialog -->
    <v-dialog v-model="backDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Confirm Navigation</v-card-title>
        <v-card-text>
          Are you sure you want to go back? Unsaved changes might be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green" dark @click="goBack">Yes</v-btn>
          <v-btn color="grey" dark @click="backDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbars -->
    <v-snackbar v-model="successSnack.show" :timeout="3000" color="green" top right>
      {{ successSnack.message }}
      <v-btn text @click="successSnack.show = false">Close</v-btn>
    </v-snackbar>
    <v-snackbar v-model="errorSnack.show" :timeout="3000" color="red" top right>
      {{ errorSnack.message }}
      <v-btn text @click="errorSnack.show = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
/* ---------- imports ---------- */
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  query,
  where,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll,
} from "firebase/storage";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";
import DisplayChecklist from "@/components/DisplayChecklist.vue";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

export default {
  components: { DisplayChecklist },
  data() {
    return {
      job: null,
      jobPhotos: [],
      deleteDialog: false,
      fieldToDeleteIndex: null,
      fileDialog: false,
      selectedFileUrl: null,
      supplierEditDialog: false,
      editSupplier: {
        index: null,
        supplier: null,
        drawingNumber: "",
        specialNote: "",
      },
      supplierFormValid: false,
      confirmationDialog: false,
      confirmedAction: null,
      confirmationMessage: "",
      customers: [],
      suppliers: [],
      engineers: [],
      technicians: [],
      salespersons: [],
      photoDialog: false,
      loadingPhotos: false,
      loading: true,
      checklistDialog: false, /* <<<<<< this drives <display-checklist> */
      backDialog: false,
      valid: false,
      hasChanges: false,
      /* serial-number state */
      primaryKey: "",
      linkKey: "NSS",
      date: "",
      code: "",
      elastomers: "",
      ibs: "",
      ibr: "",
      obs: "",
      obr: "",
      notes: "",
      /* misc */
      rules: {
        required: (v) => !!v || "Required.",
        positive: (v) => v > 0 || "Must be positive.",
        nonNegative: (v) => v >= 0 || "Cannot be negative.",
        qcPrefix: (v) => (v && v.startsWith("NSS")) || "Must start with NSS",
      },
      urgencyOptions: ["Urgent", "Medium", "Not Urgent"],
      leadTimeOptions: [
        "1-2 DAYS",
        "2-3 DAYS",
        "3-5 DAYS",
        "4-6 DAYS",
        "5-7 DAYS",
        "6-8 DAYS",
        "8-10 DAYS",
        "10-12 DAYS",
        "10-15 DAYS",
        "15-20 DAYS",
        "6-8 WEEKS",
        "8-10 WEEKS",
        "10-12 WEEKS",
        "Ex - Stock",
        "Custom",
      ],
      jobCategories: {
        stock: [
          "Bearings",
          "Bolts/Nuts",
          "Coating (Any)",
          "Die Set",
          "Gasket",
          "Grinding - no drawing",
          "Lip Seal / V-seal (not in seal)",
          "Lip Seal / V-seal (in seal or pump repair)",
          "Local Mods",
          "Machining - no drawing",
          "Material",
          "Nexus Drawn item",
          "O-Rings Only Quote",
          "O-Rings with seal",
          "Special Markup",
          "Special New",
          "Stock Item New",
          "Tools",
          "Custom",
        ],
        labour: [
          "Lapping Carbon",
          "Lapping Sic/SSic/SS/Cer",
          "Lapping TC",
          "Custom",
        ],
      },
      statuses: [
        "Inspection",
        "Costing Complete",
        "Quoted",
        "InProgress",
        "Ready",
        "Completed",
        "Delayed",
        "Canceled",
      ],
      isSalesperson: false,
      isTechnician: false,
      isAdmin: false,
      originalStatus: null,
      successSnack: { show: false, message: "" },
      errorSnack: { show: false, message: "" },
      isTablet: false,
      currentUser: null,
    };
  },
  watch: {
    job: {
      deep: true,
      handler() {
        if (this.job) {
          this.calculateGrandTotal();
          this.hasChanges = true;
        }
      },
    },
  },
  async created() {
    await this.initialize();
    await this.fetchSerialNumberData();
    await this.getPrimaryKey();
  },
  mounted() {
    this.checkTabletMode();
    window.addEventListener("resize", this.checkTabletMode);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkTabletMode);
  },
  methods: {
    /* ---------- new: open checklist dialog ---------- */
    openChecklistDialog() {
      if (this.job && this.job.jobNumber) {
        this.checklistDialog = true;
      } else {
        this.errorSnack.message = "Job number not available.";
        this.errorSnack.show = true;
      }
    },

    /* ---------- SERIAL NUMBER HELPERS ---------- */
    async getPrimaryKey() {
      try {
        const snap = await getDocs(collection(db, "SerialNumber"));
        const max =
          snap.docs
            .map((d) => d.data().primaryKey)
            .filter((k) => k && k.startsWith("NSS"))
            .map((k) => parseInt(k.slice(3), 10))
            .filter((n) => !isNaN(n))
            .reduce((a, b) => Math.max(a, b), 0) || 0;
        this.primaryKey = `NSS${max + 1}`;
      } catch (e) {
        console.error("Error fetching primary key:", e);
        this.errorSnack.message = "Failed to generate certificate number.";
        this.errorSnack.show = true;
      }
    },
    ensureQCPrefix() {
      if (this.linkKey && !this.linkKey.startsWith("NSS")) {
        this.linkKey = "NSS" + this.linkKey;
      }
    },
    async createSerialNumber() {
      try {
        await addDoc(collection(db, "SerialNumber"), {
          primaryKey: this.primaryKey,
          linkKey: this.linkKey,
          date: new Date().toISOString().split("T")[0],
          customer: this.job.customerName,
          docNumber: this.job.jobNumber,
          code: this.code,
          elastomers: this.elastomers,
          ibs: this.ibs,
          ibr: this.ibr,
          obs: this.obs,
          obr: this.obr,
          notes: this.notes || "",
        });
        this.successSnack.message = "Serial number created.";
        this.successSnack.show = true;
      } catch (e) {
        console.error("Error saving serial number:", e);
        this.errorSnack.message = "Failed to save serial number.";
        this.errorSnack.show = true;
      }
    },
    async fetchSerialNumberData() {
      if (!this.job || !this.job.jobNumber) return;
      try {
        const snap = await getDocs(collection(db, "SerialNumber"));
        const docMatch = snap.docs.find(
          (d) => d.data().docNumber === this.job.jobNumber
        );
        if (docMatch) {
          const data = docMatch.data();
          this.primaryKey = data.primaryKey || this.primaryKey;
          this.linkKey = data.linkKey || this.linkKey;
          this.date = data.date || "";
          this.code = data.code || "";
          this.elastomers = data.elastomers || "";
          this.ibs = data.ibs || "";
          this.ibr = data.ibr || "";
          this.obs = data.obs || "";
          this.obr = data.obr || "";
          this.notes = data.notes || "";
        }
      } catch (e) {
        console.error("Error fetching serial number:", e);
      }
    },

    /* ---------- GENERAL INITIALISATION ---------- */
    checkTabletMode() {
      this.isTablet = window.innerWidth <= 768;
    },
    async initialize() {
      await this.fetchCurrentUser();
      await this.fetchJobDetails();
      await Promise.all([
        this.fetchCustomers(),
        this.fetchSuppliers(),
        this.fetchEngineers(),
        this.fetchTechnicians(),
        this.fetchSalespersons(),
      ]);
      this.checkUserRole();
      this.calculateGrandTotal();
    },
    async fetchCurrentUser() {
      const auth = getAuth(firebaseApp);
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) this.currentUser = userDoc.data();
          this.checkUserRole();
        }
      });
    },

    /* ---------- FIRESTORE FETCHES ---------- */
    async fetchJobDetails() {
      const jobId = localStorage.getItem("selectedJob");
      if (!jobId) return;
      try {
        const snap = await getDoc(doc(db, "jobs", jobId));
        if (snap.exists()) {
          const d = snap.data();
          this.job = {
            jobNumber: d.jobNumber || "",
            jobName: d.jobName || "",
            ShaftSize: d.ShaftSize || "",
            customerName: d.customerName || "",
            customerNumber: d.customerNumber || "",
            engineer: d.engineer || "",
            technician: d.technician || "",
            salesperson: d.salesperson || "",
            urgency: d.urgency || "",
            setRate: d.setRate || 0,
            timeToComplete: d.timeToComplete || 0,
            status: d.status || "",
            currentDate: d.currentDate || "",
            dateQuoted: d.dateQuoted || "",
            TotalLead: d.TotalLead || "",
            salesOrderNumber: d.salesOrderNumber || "",
            invoiceNumber: d.invoiceNumber || "",
            cancellationReason: d.cancellationReason || "",
            photos: d.photos || [],
            jobDescription: d.jobDescription || "",
            JobSummary: d.JobSummary || "",
            comment: d.comment || "",
            labourItems: d.labourItems || [],
            stockItems: d.stockItems || [],
            labourSubTotal: d.labourSubTotal || 0,
            stockSubTotal: d.stockSubTotal || 0,
            grandTotal: d.grandTotal || 0,
            sampleNumber: d.sampleNumber || "",
            customerReference: d.customerReference || "",
            extraInfoFields: d.extraInfoFields || [],
          };
          this.originalStatus = this.job.status;
        }
      } catch (e) {
        console.error("Error fetching job:", e);
      }
    },
    async fetchCustomers() {
      try {
        const snap = await getDocs(collection(db, "customers"));
        this.customers = snap.docs.map((d) => ({
          companyName: d.data().companyName || "",
        }));
      } catch (e) {
        console.error("Error fetching customers:", e);
      }
    },
    async fetchSuppliers() {
      try {
        const snap = await getDocs(collection(db, "suppliers"));
        this.suppliers = snap.docs.map((d) => ({
          supplierName: d.data()["Supplier Name"] || "",
          docId: d.id,
        }));
      } catch (e) {
        console.error("Error fetching suppliers:", e);
      }
    },
    async fetchEngineers() {
      try {
        const q = query(collection(db, "users"), where("role", "==", "Engineer"));
        const snap = await getDocs(q);
        this.engineers = snap.docs.map((d) => ({
          fullName: d.data().fullName || "",
          docId: d.id,
        }));
      } catch (e) {
        console.error("Error fetching engineers:", e);
      }
    },
    async fetchTechnicians() {
      try {
        const q = query(collection(db, "users"), where("role", "==", "Technician"));
        const snap = await getDocs(q);
        this.technicians = snap.docs.map((d) => ({
          fullName: d.data().fullName || "",
          docId: d.id,
        }));
      } catch (e) {
        console.error("Error fetching technicians:", e);
      }
    },
    async fetchSalespersons() {
      try {
        const q = query(collection(db, "users"), where("role", "==", "Salesperson"));
        const snap = await getDocs(q);
        this.salespersons = snap.docs.map((d) => ({
          fullName: d.data().fullName || "",
          docId: d.id,
        }));
      } catch (e) {
        console.error("Error fetching salespersons:", e);
      }
    },

    /* ---------- ROLE CHECK ---------- */
    checkUserRole() {
      if (!this.currentUser) return;
      this.isSalesperson = this.currentUser.role === "Salesperson";
      this.isTechnician = this.currentUser.role === "Technician";
      this.isAdmin = this.currentUser.role === "Admin";
    },

    /* ---------- STATUS & SAVE ---------- */
    handleStatusChange() {
      if (this.job.status === "Quoted" && !this.job.dateQuoted) {
        this.job.dateQuoted = new Date().toISOString().split("T")[0];
      }
      if (this.job.status === "Ready") {
        this.getPrimaryKey();
        this.date = new Date().toISOString().split("T")[0];
      }
      this.calculateGrandTotal();
      this.hasChanges = true;
    },
    async saveJobDetails() {
      const jobId = localStorage.getItem("selectedJob");
      if (!jobId) return;
      try {
        await updateDoc(doc(db, "jobs", jobId), this.job);
        this.successSnack.message = "Job updated successfully.";
        this.successSnack.show = true;
        if (this.job.status === "Ready") await this.createSerialNumber();
        this.hasChanges = false;
      } catch (e) {
        console.error("Error updating job:", e);
        this.errorSnack.message = "Failed to update job.";
        this.errorSnack.show = true;
      }
    },

    /* ---------- PHOTO HANDLERS ---------- */
    async fetchChecklistPhotos(jobNumber) {
      const checklistRef = collection(db, "checklists");
      const q = query(checklistRef, where("jobNumber", "==", jobNumber));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return [];
      const checklist = querySnapshot.docs[0].data().checklist || [];
      // Flatten all section.photos arrays
      return checklist.flatMap(section => section.photos || []);
    },
    async openPhotoDialog() {
      if (!this.job || !this.job.jobNumber) {
        this.errorSnack.message = "No job number.";
        this.errorSnack.show = true;
        return;
      }
      this.photoDialog = true;
      this.loadingPhotos = true;
      try {
        this.jobPhotos = await this.fetchChecklistPhotos(this.job.jobNumber);
      } catch (e) {
        console.error("Fetch checklist photos error:", e);
        this.errorSnack.message = "Error fetching checklist photos.";
        this.errorSnack.show = true;
      } finally {
        this.loadingPhotos = false;
      }
    },
    async openAndDownloadPhotos() {
      try {
        await this.openPhotoDialog();
        if (this.jobPhotos.length) this.downloadPhotos();
      } catch (e) {
        console.error(e);
        this.errorSnack.message = "Failed to download photos.";
        this.errorSnack.show = true;
      }
    },
    async downloadPhotos() {
      if (!this.jobPhotos.length) {
        this.errorSnack.message = "No photos.";
        this.errorSnack.show = true;
        return;
      }
      const zip = new JSZip();
      const folder = zip.folder(`job_${this.job.jobNumber}_photos`);
      await Promise.all(this.jobPhotos.map((url, i) =>
        fetch(url).then((r) => r.blob()).then((b) => {
          folder.file(`photo_${i + 1}_${url.split("/").pop().split("?")[0]}`, b);
        })
      ));
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `job_${this.job.jobNumber}_photos.zip`);
      this.successSnack.message = "Photos downloaded.";
      this.successSnack.show = true;
    },

    /* ---------- TOTAL CALCULATIONS ---------- */
  calculateLabourTotalPrice(item) {
      const quantity = +item.quantity || 0;
      const markUp = +item.markUp || 0;
      const unitPrice = +item.unitPrice || 0;
      const partSize = +item.partSize || 0;

      if (item.jobCategory === "Custom") {
        item.totalPrice = quantity * unitPrice;
      } else {
        item.unitPrice = partSize * markUp;
        item.totalPrice = quantity * item.unitPrice;
      }

      this.calculateGrandTotal();
      this.hasChanges = true;
    },

    updateLabourTotalPrice(item) {
      if (item.jobCategory === "Custom") {
        item.totalPrice = (+item.quantity || 0) * item.unitPrice;
        this.calculateGrandTotal();
      }
    },
    calculateStockTotalPrice(item) {
      const qty = +item.quantity || 0;
      if (item.jobCategory !== "Custom") item.unitPrice = (+item.costPrice || 0) * item.markUp;
      item.totalPrice = qty * item.unitPrice;
      this.calculateGrandTotal();
    },
    updateStockTotalPrice(item) {
      if (item.jobCategory === "Custom") {
        item.totalPrice = (+item.quantity || 0) * item.unitPrice;
        this.calculateGrandTotal();
      }
    },
    calculateGrandTotal() {
      const labourLine = this.job.labourItems.reduce(
        (a, i) => a + (+i.totalPrice || 0),
        0
      );
      const addLab = (+this.job.setRate || 0) * (+this.job.timeToComplete || 0);
      this.job.labourSubTotal = labourLine + addLab;
      this.job.stockSubTotal = this.job.stockItems.reduce(
        (a, i) => a + (+i.totalPrice || 0),
        0
      );
      this.job.grandTotal = this.job.labourSubTotal + this.job.stockSubTotal;
    },

    /* ---------- MARKUP ---------- */
    setMarkup(item) {
      if (item.jobCategory === "Custom") item.markUp = 1;
      else {
        const map = {
          "Lapping Carbon": 4,
          "Lapping Sic/SSic/SS/Cer": 4.5,
          "Lapping TC": 5.5,
          Bearings: 1.1,
          "Bolts/Nuts": 1.5,
          "Coating (Any)": 1.1,
          "Die Set": 1.1,
          Gasket: 1.5,
          "Grinding - no drawing": 1.5,
          "Lip Seal / V-seal (not in seal)": 1.5,
          "Lip Seal / V-seal (in seal or pump repair)": 1.1,
          "Local Mods": 1.5,
          "Machining - no drawing": 1.5,
          Material: 1,
          "Nexus Drawn item": 1.8,
          "O-Rings Only Quote": 3,
          "O-Rings with seal": 1.5,
          "Special Markup": 1.5,
          "Special New": 1.3,
          "Stock Item New": 1.2,
          Tools: 1.1,
        };
        item.markUp = map[item.jobCategory] || 1.1;
      }
    },

    /* ---------- NAVIGATION ---------- */
    openBackConfirmation() {
      this.backDialog = true;
    },
    goBack() {
      this.backDialog = false;
      this.$router.go(-1);
    },

    /* ---------- CONFIRMATION ---------- */
    confirmAction(action, index = null) {
      this.confirmedAction = { action, index };
      const msg = {
        removeLabour: "Remove this labour item?",
        removeStock: "Remove this stock/part item?",
        addLabour: "Add a new labour item?",
        addStock: "Add a new stock/part item?",
        saveJobDetails: "Save changes to this job?",
      };
      this.confirmationMessage = msg[action] || "Confirm this action?";
      this.confirmationDialog = true;
    },
    executeConfirmedAction() {
      const { action, index } = this.confirmedAction;
      if (action === "removeLabour") this.job.labourItems.splice(index, 1);
      if (action === "removeStock") this.job.stockItems.splice(index, 1);
      if (action === "addLabour")
        this.job.labourItems.push({
          quantity: 1,
          partSize: 0,
          description: "",
          jobCategory: null,
          markUp: 1,
          unitPrice: 0,
          totalPrice: 0,
        });
      if (action === "addStock")
        this.job.stockItems.push({
          quantity: 1,
          code: "",
          leadTime: null,
          jobCategory: null,
          markUp: 1,
          unitPrice: 0,
          totalPrice: 0,
        });
      if (action === "saveJobDetails") this.saveJobDetails();
      this.confirmationDialog = false;
      this.calculateGrandTotal();
    },
  },
  computed: {
    isInspection() {
      return this.job && this.job.status !== "Inspection";
    },
    showSerialFields() {
      return this.job && this.job.status === "Ready";
    },
    hideLabourStock() {
      return (
        this.currentUser &&
        this.currentUser.jobs &&
        this.currentUser.jobs["View_Line_Items_(Labour_and_Stock)"] === true
      );
    },
    statusOptions() {
      return this.statuses.map((s, idx) => {
        let disabled = false;
        if (!this.isAdmin) {
          const cur = this.statuses.indexOf(this.originalStatus);
          if (s === this.originalStatus) disabled = false;
          else if (["Canceled", "Delayed"].includes(s)) disabled = false;
          else if (idx === cur + 1) disabled = false;
          else disabled = true;
        }
        return { text: s, value: s, disabled };
      });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

.view-job-container {
  background-color: #000;
  min-height: 100vh;
  position: relative;
  width: 100%;
}

.back-button {
  color: #fff;
  background: red;
  border-radius: 50%;
  position: absolute;
  top: 16px;
  left: 16px;
  transition: transform 0.3s;
}
.back-button:hover {
  transform: scale(1.1);
}

.section-title {
  font-size: 20px;
  font-weight: bold;
  margin: 22px 0 16px;
  color: red;
}

.montserrat-input {
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
}
.montserrat-button,
.montserrat-title {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
}

.v-simple-table th {
  background: #f5f5f5;
  color: #000;
  padding: 12px;
}
.v-simple-table td {
  padding: 12px;
}

@media (max-width: 600px) {
  .montserrat-input,
  .montserrat-button {
    font-size: 14px;
  }
}

.supplier-quote-item,
.journal-field-item {
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}
.supplier-quote-item + .supplier-quote-item,
.journal-field-item + .journal-field-item {
  margin-top: 16px;
}

.d-flex {
  display: flex;
}
.align-center {
  align-items: center;
}
.ml-2 {
  margin-left: 8px;
}

.v-snackbar {
  z-index: 1000;
}

.carousel-photo {
  object-fit: contain;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80vh;
  flex-direction: column;
}
</style>
